import React, { useEffect, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import {
  Space, Form, Row, Col, Image, message
} from 'antd'
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  FilterOutlined
} from '@ant-design/icons'

// Import custom components
import Button from '../../components/common/button/button'
import Modal from '../../components/common/modal/modal'
import Table from '../../components/common/table/table'
import Spinner from '../../components/common/spinner/spinner'
import PrintPreview from '../../components/feature/printPreview/printPreview'

// Import assets
import a4Printer from '../../assets/images/a4Printer.png'
import labelPrinter from '../../assets/images/labelPrinter.png'

import {
  handleSaveEdit,
  validateEditChange,
  processPrintData,
  exportMissingAPNs,
  getSavedLabelsColumns,
  getEditColumns,
  getMissingAPNColumns,
  handlePrint,
  createPageStyle,
  fetchDataUtil,
  handleDeleteSavedItems,
  getColumnSearchProps,
  getDateFilterProps,
  getCustomFilterWithSearch
} from '../savedLabels/savedShelfEdgeLabelsUtils'

// Import styles
import './SavedShelfEdgeLabels.scss'

const SavedLabels = () => {
  // State management
  const [isReloading, setIsReloading] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [rows, setRows] = useState([])
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editRowData, setEditRowData] = useState(null)
  const [missingAPNModalVisible, setMissingAPNModalVisible] = useState(false)
  const [productData, setProductData] = useState([])
  const [printList, setPrintList] = useState([])
  const [selectedPrinter, setSelectedPrinter] = useState('A4')
  const [printModalVisible, setPrintModalVisible] = useState(false)
  const [editSelectedRows, setEditSelectedRows] = useState([])
  const [messageApi, contextHolder] = message.useMessage()
  const [flag, setFlag] = useState(false)
  const [missingAPNs, setMissingAPNs] = useState([]) // Always initialize as empty array
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [popupDeleteModalVisible, setPopupDeleteModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showPrintPreview, setShowPrintPreview] = useState(false)
  const [pageSize, setPageSize] = useState(10)
  const [tableFilters, setTableFilters] = useState({})

  const formEditRow = Form.useForm()[0]

  // References
  const printPreviewRef = useRef(null)
  const hasFetched = useRef(false)

  // MSAL instance
  const { instance } = useMsal()

  // Add and remove print style when printer type changes
  useEffect(() => {
    let styleElement = document.getElementById('print-style-element')

    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = 'print-style-element'
      document.head.appendChild(styleElement)
    }

    styleElement.textContent = createPageStyle(selectedPrinter)

    return () => {
      if (styleElement) {
        document.head.removeChild(styleElement)
      }
    }
  }, [selectedPrinter])

  // Fetch data function for reuse
  const fetchData = async () => {
    await fetchDataUtil(instance, setRows, setIsReloading, messageApi)
  }

  // Initial data load
  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    fetchData()
  }, [])

  // Process product data when available - FIXED VERSION
  useEffect(() => {
    if (!flag) return

    if (productData.length > 0) {
      // Use the new processPrintData that returns an object
      const { missingAPNs: missingAPNsTemp } = processPrintData(printList, productData)

      // Safety check - ensure missingAPNsTemp is an array
      if (Array.isArray(missingAPNsTemp)) {
        setMissingAPNs(missingAPNsTemp)
      } else {
        console.warn('processPrintData did not return an array for missingAPNs:', missingAPNsTemp)
        setMissingAPNs([])
      }

      setPrintModalVisible(true)
    } else {
      messageApi.error('No products found. Please check your selection.')
    }
  }, [productData, printList, messageApi, flag])

  // Handle print button click - UPDATED VERSION
  const handlePrintClick = async (printrow) => {
    try {
      const result = await handlePrint(
        printrow,
        instance,
        setIsReloading,
        setProductData,
        setFlag,
        setPrintList,
        messageApi,
        setMissingAPNs // Pass the setter function
      )

      if (result) {
        // The flag is now set and the useEffect will take care of showing the modal
      }
    } catch (error) {
      console.error('Print error:', error)
      messageApi.error('Failed to generate labels')
    }
  }

  // Handle edit button click
  const handleMainEdit = (row) => {
    setEditRowData({ ...row, guid: row.id })
    setEditSelectedRows([])
    setEditModalVisible(true)
  }

  // Handle saving edits
  const handleSaveUtilClick = async () => {
    try {
      await handleSaveEdit(
        formEditRow,
        editRowData,
        setLoading,
        setIsReloading,
        instance,
        setEditModalVisible,
        messageApi,
        fetchData
      )
    } catch {
      messageApi.error('Failed to save changes')
      setLoading(false)
      setIsReloading(false)
    }
  }

  // Handle editing table data
  const handleEditTableChange = (value, record, field) => {
    if (!validateEditChange(field, value, messageApi)) return

    const updatedValue = field === 'quantity' ? parseInt(value, 10) : value

    const updatedPrintList = editRowData.printList.map((item) =>
      item.apn === record.apn ? { ...item, [field]: updatedValue } : item
    )

    setEditRowData({ ...editRowData, printList: updatedPrintList })
  }

  // Handle deleting selected rows from edit modal
  const handleDeleteSelectedPopupRows = () => {
    const selectedIds = editSelectedRows.map((row) => row.apn)
    setEditRowData((prevData) => ({
      ...prevData,
      printList: prevData.printList.filter((row) => !selectedIds.includes(row.apn))
    }))
    setPopupDeleteModalVisible(false)
    setEditSelectedRows([])
  }

  // Handle modal states
  const handleMissingAPNModalOpen = () => setMissingAPNModalVisible(true)
  const handleMissingAPNModalClose = () => setMissingAPNModalVisible(false)
  const handleDeleteClose = () => setDeleteModalVisible(false)
  const handlePopupDeleteClose = () => setPopupDeleteModalVisible(false)

  // Handle pagination change
  const handlePageSizeChange = (current, size) => {
    setPageSize(size)
  }

  // Handle printer selection
  const handlePrinterSelection = (printer) => setSelectedPrinter(printer)

  // Handle delete button click
  const handleDeletePopup = () => {
    if (selectedRows.length > 0) {
      setDeleteModalVisible(true)
    } else {
      messageApi.warning('Please select items to delete')
    }
  }

  // Handle delete button click in edit modal
  const handlePopupDeletePopup = () => {
    if (editSelectedRows.length > 0) {
      setPopupDeleteModalVisible(true)
    } else {
      messageApi.warning('Please select items to delete')
    }
  }

  // Handle deleting saved items
  const deleteSavedItems = async () => {
    await handleDeleteSavedItems(
      selectedRows,
      setLoading,
      setIsReloading,
      instance,
      setRows,
      setSelectedRows,
      setDeleteModalVisible,
      messageApi
    )
  }

  // Handle table changes (including filters)
  const handleTableChange = (pagination, filters) => {
    setTableFilters(filters)
  }

  // Clear all filters
  const handleClearAllFilters = () => {
    setTableFilters({})
  }

  // Get unique created by values for filter dropdown
  const createdByOptions = [...new Set(rows.map(item => item.createdBy))]
    .filter(Boolean)

  const handlePrintSelection = () => {
    setPrintModalVisible(false)
    setShowPrintPreview(true)

    setTimeout(() => {
      const styleElement = document.createElement('style')
      styleElement.id = 'print-fixes-style'

      styleElement.textContent = `
      @media print {
        @page {
          size: ${selectedPrinter === 'Zebra' ? '7cm 4cm' : 'A4'};
          margin: 0;
        }
        
        html, body {
          height: auto !important;
          overflow: visible !important;
        }
        
        .print-preview__A4container {
          page-break-after: always !important;
          page-break-inside: avoid !important;
        }
        
        .print-preview__Zebra-container {
          page-break-after: always !important;
          page-break-inside: avoid !important;
        }
      }
    `

      document.head.appendChild(styleElement)
      window.print()

      setTimeout(() => {
        if (document.getElementById('print-fixes-style')) {
          document.head.removeChild(document.getElementById('print-fixes-style'))
        }
        setShowPrintPreview(false)
      }, 500)
    }, 300)
  }

  // Table columns with proper filters
  const baseColumns = getSavedLabelsColumns(handleMainEdit, handlePrintClick, isReloading)
  const editColumns = getEditColumns(handleEditTableChange)
  const missingAPNColumns = getMissingAPNColumns()

  const enhancedColumns = [
    {
      ...baseColumns[0], // Request Name column
      dataIndex: 'listName',
      filteredValue: tableFilters.listName || null,
      ...getColumnSearchProps('listName', 'Request Name')
    },
    {
      ...baseColumns[1], // Created By column
      dataIndex: 'createdBy',
      filteredValue: tableFilters.createdBy || null,
      ...getCustomFilterWithSearch('createdBy', createdByOptions)
    },
    {
      ...baseColumns[2], // Created Date column
      dataIndex: 'createdDate',
      filteredValue: tableFilters.createdDate || null,
      ...getDateFilterProps()
    },
    baseColumns[3], // Expires At column (no filter needed)
    baseColumns[4] // Actions column (no filter needed)
  ]

  // Table selection configurations
  const rowSelection = {
    selectedRowKeys: selectedRows.map(item => item.id),
    onChange: (_, selectedItems) => setSelectedRows(selectedItems)
  }

  const editRowSelection = {
    selectedRowKeys: editSelectedRows.map(item => item.apn),
    onChange: (_, selectedItems) => setEditSelectedRows(selectedItems)
  }

  // Sort data by created date (newest first)
  const sortedData = [...rows].sort((a, b) =>
    new Date(b.createdDate) - new Date(a.createdDate)
  )

  const hasFiltersApplied = Object.keys(tableFilters).some(key =>
    tableFilters[key]?.length > 0
  )

  const missingAPNsTableData = Array.isArray(missingAPNs)
    ? missingAPNs.map((apn, index) => ({
      id: index + 1,
      apn,
      key: index
    }))
    : []

  return (
    <>
      {contextHolder}

      <div className="saved-labels">
        <h4 className="saved-labels-title">Saved Shelf Edge Labels</h4>
        <hr />

        {/* Filter summary and clear button */}
        {hasFiltersApplied && (
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button
              onClick={handleClearAllFilters}
              icon={<FilterOutlined />}
              type="default"
              size="small"
            >
              Clear All Filters
            </Button>
          </div>
        )}

        <div className="saved-labels-container">
          <Button
            type="default"
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeletePopup}
            disabled={selectedRows.length === 0}
            className="saved-labels-delete-btn"
          >
            Delete
          </Button>

          {/* Wrap table area with Spinner for localized loading */}
          <div style={{ position: 'relative', minHeight: '400px' }}>
            {isReloading && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Spinner style={{ transform: 'scale(1.5)' }} />
              </div>
            )}
            <Table
              rowSelection={rowSelection}
              dataSource={sortedData}
              columns={enhancedColumns}
              rowKey="id"
              onChange={handleTableChange}
              pagination={{
                pageSize: pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                onChange: (page, pageSize) => {
                  setPageSize(pageSize)
                },
                onShowSizeChange: handlePageSizeChange,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
              }}
              className="saved-labels-table"
              scroll={{ x: 'max-content', y: 550 }}
              style={{ opacity: isReloading ? 0.5 : 1 }}
            />
          </div>
        </div>

        {/* Edit Modal */}
        <Modal
          title="Edit Saved Items"
          open={editModalVisible}
          onCancel={() => setEditModalVisible(false)}
          width={700}
          footer={[
            <Button key="cancel" onClick={() => setEditModalVisible(false)}>
              Cancel
            </Button>,
            <Button
              key="save"
              type="primary"
              onClick={handleSaveUtilClick}
              loading={loading}
            >
              Save
            </Button>
          ]}
          className="saved-labels-edit-modal"
        >
          {editRowData && (
            <Form form={formEditRow} layout="vertical">
              <Button
                type="default"
                danger
                icon={<DeleteOutlined />}
                onClick={handlePopupDeletePopup}
                disabled={editSelectedRows.length === 0}
                className="saved-labels-edit-delete-btn"
              >
                Delete
              </Button>

              <Table
                rowSelection={editRowSelection}
                dataSource={editRowData.printList}
                columns={editColumns}
                rowKey="apn"
                pagination={false}
                className="saved-labels-edit-table"
                scroll={{ y: 350 }}
              />
            </Form>
          )}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          title={<Space><ExclamationCircleOutlined className="confirmation-icon" /> Confirm Deletion</Space>}
          open={deleteModalVisible}
          onCancel={handleDeleteClose}
          footer={[
            <Button key="cancel" onClick={handleDeleteClose}>
              Cancel
            </Button>,
            <Button
              key="delete"
              type="default"
              danger
              onClick={deleteSavedItems}
              loading={loading}
            >
              Delete
            </Button>
          ]}
          className="saved-labels-confirmation-modal"
        >
          <p>
            Do you want to delete {selectedRows.length} Saved {selectedRows.length === 1 ? 'Label' : 'Labels'}?
          </p>
        </Modal>

        {/* Delete Items from Edit Modal Confirmation */}
        <Modal
          title={<Space><ExclamationCircleOutlined className="confirmation-icon" /> Confirm Deletion</Space>}
          open={popupDeleteModalVisible}
          onCancel={handlePopupDeleteClose}
          footer={[
            <Button key="delete" type="default" danger onClick={handleDeleteSelectedPopupRows}>
              Delete
            </Button>,
            <Button key="cancel" onClick={handlePopupDeleteClose}>
              Cancel
            </Button>
          ]}
          className="saved-labels-confirmation-modal"
        >
          <p>Do you want to delete {editSelectedRows.length} APN{editSelectedRows.length === 1 ? '' : 's'}?</p>
        </Modal>

        {/* Missing APNs Modal - FIXED VERSION */}
        <Modal
          title={
            <div className="saved-labels-missing-header">
              <span>Invalid APNs</span>
              <Space>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    if (Array.isArray(missingAPNs)) {
                      exportMissingAPNs(missingAPNs)
                    } else {
                      messageApi.error('No invalid APNs to export')
                    }
                  }}
                >
                  Download
                </Button>
              </Space>
            </div>
          }
          open={missingAPNModalVisible}
          onCancel={handleMissingAPNModalClose}
          footer={null}
          className="saved-labels-missing-modal"
        >
          <Table
            dataSource={missingAPNsTableData}
            columns={missingAPNColumns}
            pagination={false}
            scroll={{ y: 300 }}
            className="saved-labels-missing-table"
          />
        </Modal>

        {/* Print Selection Modal - FIXED VERSION */}
        <Modal
          title="Select Printer"
          open={printModalVisible}
          onCancel={() => setPrintModalVisible(false)}
          footer={[
            <Button
              key="print"
              type="primary"
              disabled={!selectedPrinter}
              onClick={handlePrintSelection}
            >
              Confirm & Print
            </Button>
          ]}
          className="saved-labels-print-modal"
        >
          <Row justify="space-around" align="middle">
            <Col span={11}>
              <div
                className={`printer-option ${selectedPrinter === 'A4' ? 'selected' : ''}`}
                onClick={() => handlePrinterSelection('A4')}
              >
                <Image src={a4Printer} alt="A4 Printer" width={100} preview={false} />
                <p>A4 Printer</p>
              </div>
            </Col>
            <Col span={11}>
              <div
                className={`printer-option ${selectedPrinter === 'Zebra' ? 'selected' : ''}`}
                onClick={() => handlePrinterSelection('Zebra')}
              >
                <Image src={labelPrinter} alt="Zebra Printer" width={100} preview={false} />
                <p>Zebra Printer</p>
              </div>
            </Col>
          </Row>

          {Array.isArray(missingAPNs) && missingAPNs.length > 0 && (
            <Row justify="start" className="invalid-apn-row">
              <Button
                type="link"
                danger
                onClick={handleMissingAPNModalOpen}
              >
                View invalid APN list
              </Button>
            </Row>
          )}
        </Modal>

        {/* Hidden Print Content - only shows during actual printing */}
        {showPrintPreview && (
          <div className="print-only-content">
            <PrintPreview
              ref={printPreviewRef}
              products={productData}
              printList={printList}
              selectedPrinter={selectedPrinter}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default SavedLabels